import Paragraphs from '@render-props/paragraphs';
import { useEffect, useRef, useState } from 'react';

export interface IToggleText {
  text: string;
  clamp?: number;
  expandLabel?: string;
  collapseLabel?: string;
  className?: string;
}

function ToggleText({
  text,
  className,
  clamp = 5,
  expandLabel = 'Read more',
  collapseLabel = 'Read less',
}: IToggleText) {
  const textElement = useRef<HTMLParagraphElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [disableExpand, setDisableExpand] = useState(true);

  useEffect(() => {
    const element = textElement?.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;

      if (!isExpanded) {
        const { scrollHeight, clientHeight } = entry.target;
        setDisableExpand(scrollHeight <= clientHeight);
      }
    });

    observer.observe(element);

    return () => {
      // Cleanup the observer by unobserving all elements
      observer.disconnect();
    };
  }, [clamp, isExpanded, text, textElement]);

  return (
    <div className={className}>
      {/* Set a default clamp value of 1 and use the clamp prop as style
       * since we cannot set it as dynamic arbitrary tailwind class */}
      <div
        data-testid="text"
        ref={textElement}
        className={`space-y-4 overflow-hidden ${
          !isExpanded ? `line-clamp-1` : 'line-clamp-none'
        }`}
        style={{ WebkitLineClamp: clamp }}
      >
        <Paragraphs text={text}>
          {({ key, text }: { key: number; text: string }) => (
            <p key={key}>{text}</p>
          )}
        </Paragraphs>
      </div>
      {!disableExpand && (
        <button
          data-testid="toggle-cta"
          type="button"
          className="text-base font-normal text-bolt hover:underline"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  );
}

export default ToggleText;
